import { z } from 'zod';
import { REGEX } from '@/shared/constants/regex';

/**
 * El LENGUAJE completo del onboarding v3. Un flow es un archivo JSON validado
 * contra este schema; lo que no cabe aquí, no existe. De este contrato derivan
 * el runtime, el walk, la transición (client hoy, heru-user-management-engine en fase 0b),
 * el tracking, el spec, los tests generativos y el mapa dev.
 *
 * Reglas del lenguaje (las estructurales las verifica validateFlow):
 * - `next` tiene UNA forma: nodeId directo, o mapa respuesta/outcome → nodeId.
 * - `skip` es OBLIGATORIO en todo nodo no-terminal: 'none' | 'next' (sugar del
 *   next único) | un nodeId explícito.
 * - El grafo es un DAG; todo nodo alcanza un terminal; todo nodo es alcanzable.
 *   La única arista hacia atrás es la de un info `returns`, que no avanza:
 *   regresa a la pregunta que lo abrió (ver infoNode).
 * - Las condiciones son un DSL cerrado sobre answers y llaves de ctx enumeradas.
 * - Los ids 'none' | 'next' están reservados (no pueden ser nodos).
 * - Capacidad sin consumidor se BORRA: cada campo de este schema debe tener al
 *   menos un flow que lo use y un render/regla que lo lea. Nada "por si acaso".
 */

// ── Contexto ──
// Llaves de contexto externas al flow — el ÚNICO canal de lectura del mundo
// exterior (condiciones, prefills e interiores de actions). Cerrado a
// propósito: agregar una llave = agregarla aquí + proveerla en el host
// (sandbox: panel editable; prod: derivada del profile/servicios; fase 0b:
// computada por el server). Cada llave declara su tipo (control del sandbox) y
// su grupo (categoría del panel); el ORDEN de este objeto es el orden del
// panel.
export const CTX_GROUP_LABELS = {
	signup: 'Registro',
	linking: 'Vinculación SAT',
	booking: 'Asesoría',
	plan: 'Plan',
	ally: 'Aliados',
	goals: 'Objetivos',
} as const;
export type CtxGroup = keyof typeof CTX_GROUP_LABELS;

type CtxKeySpec = { type: 'boolean' | 'string'; group: CtxGroup };

export const CTX_SPEC = {
	/** Datos ya conocidos del usuario — prefills de inputs. */
	fullName: { type: 'string', group: 'signup' },
	email: { type: 'string', group: 'signup' },
	cellphone: { type: 'string', group: 'signup' },
	/** Cómo entró el usuario: la vía de registro ya verificó ese dato, así que
	 * el nodo que lo pregunta se oculta con visibleWhen (Google → hasEmail,
	 * teléfono → hasPhone). */
	hasEmail: { type: 'boolean', group: 'signup' },
	hasPhone: { type: 'boolean', group: 'signup' },
	/** Ya aceptó Términos y Condiciones (v2 lo estampa como heru_tcs). */
	acceptedTcs: { type: 'boolean', group: 'signup' },
	/** Experimento phone-verification-optional: el usuario cayó en el brazo
	 * treatment (stamp en el record — única fuente, sin kill switch por flag).
	 * La pantalla de teléfono guarda el número SIN OTP (cellphone_verified=false
	 * server-side); si el número pertenece a otra cuenta cae al reto OTP normal
	 * (código o merge). Los T&C se aceptan igual, así que el signup no cambia. */
	phoneOptional: { type: 'boolean', group: 'signup' },
	/** Experimento regularize-quote-first: el usuario cayó en el brazo
	 * treatment (stamp en el record — única fuente). El flujo le pregunta sus
	 * regímenes y desde cuándo no presenta, y le cotiza con eso ANTES de pedir
	 * la vinculación; el control sigue vinculando primero. */
	quoteBeforeLinking: { type: 'boolean', group: 'linking' },
	isLinked: { type: 'boolean', group: 'linking' },
	/** Workflow de vinculación EN CURSO (enviado, sin resolver). Los nodos del
	 * formulario se ocultan mientras esté activa — si el workflow FALLA el host
	 * la baja y los formularios reaparecen (back aterriza ahí y se reintenta).
	 * Por eso la vinculación NO es un action irreversible: su inaccesibilidad
	 * es condición viva del mundo, no un hecho consumado del flow. */
	isLinking: { type: 'boolean', group: 'linking' },
	/** Vinculado pero con la sincronización inicial del SAT en curso (~10 min
	 * post-vinculación): su data fiscal AÚN no está disponible. Estados del
	 * dominio: no vinculado → vinculado sincronizando → vinculado. Las preguntas
	 * que sustituyen data del SAT (regímenes pendientes, periodos) se muestran
	 * mientras isLinked=false O isSyncing=true — solo un vinculado ya
	 * sincronizado puede saltárselas. */
	isSyncing: { type: 'boolean', group: 'linking' },
	/** Veredictos del fiscal check canónico (resolveFiscalCheck sobre el
	 * taxpayer — con fallback a credenciales subidas). El perfil Zubale pinta
	 * sus checks/cards de e.firma y sellos CSD desde aquí, no de estado local:
	 * subir en los uploaders reales refresca el taxpayer y estos flips. */
	efirmaActive: { type: 'boolean', group: 'linking' },
	csdActive: { type: 'boolean', group: 'linking' },
	/** El CSD está cargado pero su vigencia ya pasó (csdActive es false en ese
	 * caso). Solo cambia el copy del hub del aliado: "renueva" en vez de
	 * "sube". Lo deriva el host con el reloj (deriveAllyHubTaxpayerVerdicts). */
	csdExpired: { type: 'boolean', group: 'linking' },
	/** RFC en estado "ACTIVO" estricto según el fiscal check canónico (paridad
	 * con el ZubalePerfilStep v2: active && status === 'ACTIVO'; un REACTIVADO
	 * u otro estado pinta warn). El perfil Zubale lee su check de aquí. */
	rfcActive: { type: 'boolean', group: 'linking' },
	/** Alta en Plataformas Tecnológicas (625) en los last_regimes del fiscal
	 * check (paridad v2: presencia del código, sin filtrar end_date). */
	platecRegimeActive: { type: 'boolean', group: 'linking' },
	/** Vigencias formateadas ("14 mar 2027") de e.firma/CSD y nombre del primer
	 * régimen del taxpayer — presentación de los checks del perfil Zubale
	 * (paridad v2: "vigente hasta el X" / "Estás en {régimen}"). Los formatea el
	 * host (la pantalla pura no puede tocar el reloj); '' = sin dato. */
	efirmaValidTo: { type: 'string', group: 'linking' },
	csdValidTo: { type: 'string', group: 'linking' },
	primaryRegimeName: { type: 'string', group: 'linking' },
	// Los regímenes que YA sabemos del usuario (extracción del CURP/RFC),
	// como slugs del flow separados por coma. Sólo PRE-LLENA: nunca aparece en
	// un visibleWhen, así que no multiplica el espacio de caminos del walk.
	detectedRegimes: { type: 'string', group: 'linking' },
	/** RFC con el que la pantalla `sat-ciec` prellena su campo, y su estado:
	 * 'verified' (el SAT lo confirmó) · 'unverified' (lo calculamos con la CURP)
	 * · '' (nada que prellenar: el campo sale vacío). Lo resuelve el host
	 * (useRfcPrefill: user-management-engine con respaldo en la extracción por
	 * CURP en curso). Sólo PRE-LLENA, como detectedRegimes: nunca aparece en un
	 * visibleWhen. */
	rfcPrefill: { type: 'string', group: 'linking' },
	rfcPrefillStatus: { type: 'string', group: 'linking' },
	/** Estado del trámite de sellos (CSD) que heru corrió por el usuario, cuando
	 * el SAT AÚN no los reporta activos: '' = sin trámite (o ya activos) ·
	 * 'pending_sat' = tramitados hace ≤ 72 h, el SAT los está activando ·
	 * 'overdue' = tramitados hace > 72 h y siguen sin activarse. Lo deriva el
	 * host (necesita el reloj) desde `taxpayer.csd_requested_at`; el check de
	 * sellos del hub del aliado pinta ámbar "En espera del SAT" en el primero
	 * y vuelve a "Requiere atención" con su propio porqué en el segundo. */
	csdRequestStatus: { type: 'string', group: 'linking' },
	/** Ya tiene una asesoría fiscal agendada (record `onboarding_call` con
	 * status booked). El interior de advisory-booking pinta el estado agendado
	 * en vez del calendario. */
	hasBooking: { type: 'boolean', group: 'booking' },
	/** Ya tiene un plan mensual activo. Es la VERDAD del plan para las pantallas
	 * (el plan del aliado se pinta "activo" con este flag) y oculta el upsell
	 * genérico de plan (visibleWhen), cuyo passthrough sale a home. La compra NO
	 * es una posición del grafo: el éxito vive en el drawer y el hecho vive aquí. */
	hasActivePlan: { type: 'boolean', group: 'plan' },
	/** El plan mensual que le recomienda toda la web ('plataformas' |
	 * 'profesionista' | 'empresarial'), resuelto por el host con la MISMA lógica
	 * que el inicio y /planes (SAT, CURP, lo declarado…). Los terminales de plan
	 * y de compra lo pitchean tal cual. '' = sin recomendación. */
	recommendedPlan: { type: 'string', group: 'plan' },
	/** La campaña de un aliado (`?partner=`) ya contestó pasos por el usuario
	 * —objetivo, aliado y cómo nos conoció— y él TODAVÍA no llega a su alianza.
	 * Esos nodos se ocultan mientras dure: preguntar es distinto de saber, y
	 * mostrarle el grid con su aliado pre-seleccionado para que lo repita es
	 * pedirle que confirme lo que su link ya dijo.
	 *
	 * Es ctx y no una condición sobre el answer porque el avance EN VIVO del
	 * motor es posicional —aterriza en el siguiente nodo visible, respondido o
	 * no— y solo la invisibilidad lo hace pasar de largo.
	 *
	 * SE APAGA en cuanto el usuario entra a su alianza (o completa). No es un
	 * detalle: mientras fue permanente, quien llegó una vez por una campaña no
	 * volvía a ver el grid NUNCA —ni por «Aliados oficiales», ni por el back, ni
	 * re-eligiendo «Me recomendó un aliado»— y quedaba casado con ese aliado de
	 * por vida. Ahorrarle la pregunta al entrar es correcto; quitarle la
	 * decisión para siempre, no. */
	campaignPrefilled: { type: 'boolean', group: 'ally' },
	/** El usuario llegó por la campaña de un aliado que NO está en el grid
	 * (`?partner=admattic`) — o sea, por un link pagado a una oferta concreta.
	 *
	 * Apaga el botón de volver. La razón es de negocio, no de UI: a diferencia de
	 * `campaignPrefilled`, esto NO se apaga al entrar a la alianza, y ahí está el
	 * punto. Al contestar `ally_email` el grid y goals vuelven a ser visibles, así
	 * que desde la pantalla del plan bastaban cuatro toques de «atrás» para
	 * aterrizar al usuario en el grid de los otros 19 aliados —donde su propio
	 * aliado ni siquiera aparece, porque Admattic no es una choice—. Por ese clic
	 * pagamos comisión: sacarlo de la oferta que vino a ver es regalar el tráfico.
	 *
	 * Solo aplica a aliados FUERA del grid. Quien entró eligiendo Uber en el grid
	 * llegó por su cuenta y volver es legítimo: ahí no hay nadie cobrándonos. */
	campaignLocked: { type: 'boolean', group: 'ally' },
	/** Estado de la validación fiscal por CURP del usuario, para el requisito de
	 * CURP del hub de cumplimiento del aliado (DiDi): '' = sin CURP capturado;
	 * 'pending'/'running' = validación en vuelo (polling de CurpExtraction);
	 * 'done'/'failed' = estado final. Lo deriva el host del MISMO contexto de
	 * extracción que usa el banner global — no un canal paralelo. */
	curpValidationStatus: { type: 'string', group: 'ally' },
	/** Veredicto de elegibilidad para facturación a cuenta de terceros (fc3) que
	 * la validación por CURP reporta (CurpExtractionState.fc3_eligible):
	 * '' = aún sin veredicto · 'ok' = elegible · 'warn' = no elegible. */
	fc3Status: { type: 'string', group: 'ally' },
	/** Veredicto de la actividad económica de reparto de bienes o alimentos
	 * (requisito DiDi). Mismo contrato tri-estado que fc3Status. La validación
	 * por CURP no trae la actividad (F2 de t-056): el
	 * host devuelve '' y el check pinta "desconocido" con su porqué. Desde t-279
	 * (13-oct) el host lo deriva de la CSF cuando ya existe (activity_descs,
	 * tras vincular la e.firma); sin CSF sigue en ''. */
	repartoActivityStatus: { type: 'string', group: 'ally' },
	/** Requisitos del aliado que la validación por CURP ya cruza sin vincular el
	 * SAT (DiDi, t-279): mismo contrato tri-estado que fc3Status ('' / 'ok' /
	 * 'warn'). Los valores acompañan el copy del check. Ver
	 * host/curpRequirements.ts. */
	/** El caso del socio con su aliado en worker-compliance está `compliant`
	 * (t-338): el hub del aliado lo da por cumplido sin importar el CURP. */
	allyPartnerCompliant: { type: 'boolean', group: 'ally' },
	/** El CURP del record es el de la e.firma vinculada (heru lo tomó de ahí, el
	 * socio no lo capturó): el paso del CURP lo dice así. */
	curpFromEfirma: { type: 'boolean', group: 'ally' },
	curpRfcStatus: { type: 'string', group: 'ally' },
	curpRfc: { type: 'string', group: 'ally' },
	curpEfirmaStatus: { type: 'string', group: 'ally' },
	curpEfirmaValidTo: { type: 'string', group: 'ally' },
	curpCsdStatus: { type: 'string', group: 'ally' },
	curpCsdValidTo: { type: 'string', group: 'ally' },
	/** Sellos (CSD) cargados en heru (DiDi v2): '' / 'ok' / 'warn'. Ver
	 * deriveCsdEnHeruStatus en host/curpRequirements.ts. */
	csdEnHeruStatus: { type: 'string', group: 'ally' },
	/** Régimen de plataformas tecnológicas (625) entre los de la validación. */
	curpPlatecRegimeStatus: { type: 'string', group: 'ally' },
	/** "Sueldos y salarios (605) y RESICO (626)". */
	curpRegimesLabel: { type: 'string', group: 'ally' },
	/** El usuario entró al host por la superficie permanente de aliados (la URL
	 * de entrada nombra el grid: "Aliados oficiales" en shell, perfil, o el
	 * deep-link directo). Su intención es la alianza, no el registro: las
	 * preguntas prescindibles del spine (discovery_source, curp) se ocultan para
	 * que el walk las pase de largo y la re-entrada aterrice en goals → grid.
	 *
	 * Es ctx y no una condición sobre answers por la misma razón que
	 * campaignPrefilled: el avance del motor es posicional y solo la
	 * invisibilidad lo hace pasar de largo. El host lo captura UNA vez al montar
	 * (ref — estable toda la sesión: si cambiara al navegar, el path del walk se
	 * recalcularía bajo los pies del usuario) y NADA se persiste: el resume
	 * normal del onboarding vuelve a mostrar las preguntas. */
	allyEntry: { type: 'boolean', group: 'ally' },
	/** Monto de heru-cash prometido por vincular, como texto ('100'), o '' si no
	 * hay promesa. Es un experimento de 2 brazos (control/$100) y el monto ES
	 * el tratamiento que se mide, así que tiene que anunciarse ANTES de vincular.
	 *
	 * Vive en ctx porque resolverlo NO es puro: hay que evaluar GrowthBook y
	 * registrar la promesa en ventas. El host lo resuelve (useLinkingReward) y la
	 * pantalla solo pinta el número — mismo reparto que efirmaValidTo, que
	 * tampoco puede formatearse en zona pura.
	 *
	 * String y no number porque FlowCtx es boolean|string, y la pantalla lo pinta
	 * tal cual sin aritmética. De paso queda disponible para `visibleWhen`: se
	 * podría ramificar el grafo por brazo sin tocar código. */
	linkingRewardAmount: { type: 'string', group: 'linking' },
	/** The `onboarding-solo-facturar` flag (GrowthBook, resolved by the host).
	 * It gates the goals choice «Solo me interesa facturar» through the
	 * choice's `visibleWhen`; off, the goals grid is exactly today's. */
	invoicingOnlyEnabled: { type: 'boolean', group: 'goals' },
	/** The `onboarding-solo-facturar-empresas` flag (GrowthBook, resolved by the
	 * host). It gates the person-type question of the invoicing-only branch
	 * through the node's `visibleWhen`: off, the question is passed through as a
	 * physical person and the company notice is unreachable, kept for when
	 * invoicing supports companies. */
	invoicingCompaniesEnabled: { type: 'boolean', group: 'goals' },
	/** The invoicing-only branch sells an invoicing plan after the seals
	 * connect only to whom still has the free plan with plans to buy (quota
	 * `source: default`, behind the quota flag). A bought plan, one a tax plan
	 * includes or an unanswered quota hides the offer through the node's
	 * `visibleWhen`, and the walk goes on to invoicing. Like `hasActivePlan`,
	 * the purchase is not a position of the graph: once paid the quota stops
	 * being the free one and the node is passed through. */
	invoicingPlanOffered: { type: 'boolean', group: 'plan' },
} as const satisfies Record<string, CtxKeySpec>;
export type CtxKey = keyof typeof CTX_SPEC;
export const CTX_KEYS = Object.keys(CTX_SPEC) as [CtxKey, ...CtxKey[]];
export type FlowCtx = Partial<Record<CtxKey, boolean | string>>;

// ── Sync effects (registro cerrado) ──
// Escrituras al mundo exterior declaradas por nodo — el ÚNICO canal de salida.
// El reducer NUNCA ejecuta IO: al responderse un nodo con `sync`, devuelve el
// efecto en TransitionResult.effects y el dueño del estado lo ejecuta (sandbox:
// solo lo muestra; prod: host contra el service; fase 0b: el server en la misma
// transacción que la transición).
export const SYNC_KEYS = ['profile-name', 'ally-org-link', 'curp-extraction'] as const;
export type SyncKey = (typeof SYNC_KEYS)[number];

// ── Gates (registro cerrado) ──
// Escritura al mundo que VALIDA: a diferencia de un sync (best-effort, corre
// después de avanzar), un gate corre ANTES del submit vía el puerto de services
// y solo su éxito deja avanzar — un rechazo de dominio (correo ya registrado en
// otra cuenta) se pinta inline y la pantalla se queda. El reducer no lo conoce:
// el nodo respondido no emite efecto; la persistencia ya ocurrió en el gate.
export const GATE_KEYS = ['profile-email', 'ally-link-verify'] as const;
export type GateKey = (typeof GATE_KEYS)[number];

// ── Condiciones (DSL cerrado) ──
const literal = z.union([z.string(), z.boolean()]);

export type Condition =
	| { answer: string; eq: string | boolean }
	| { answer: string; neq: string | boolean }
	| { ctx: CtxKey; eq: string | boolean }
	| { and: Condition[] }
	| { or: Condition[] };

export const conditionSchema: z.ZodType<Condition> = z.lazy(() =>
	z.union([
		z.object({ answer: z.string(), eq: literal }).strict(),
		z.object({ answer: z.string(), neq: literal }).strict(),
		z.object({ ctx: z.enum(CTX_KEYS), eq: literal }).strict(),
		z.object({ and: z.array(conditionSchema).min(1) }).strict(),
		z.object({ or: z.array(conditionSchema).min(1) }).strict(),
	]),
);

// ── Edges ──
// nodeId directo (edge único) o mapa valor-de-respuesta/outcome → nodeId.
const nextSchema = z.union([z.string(), z.record(z.string(), z.string())]);

// 'none' (no se puede saltar) | 'next' (sugar: sigue el next único del nodo)
// | nodeId explícito.
const skipSchema = z.string();

export const RESERVED_IDS = ['none', 'next'] as const;

// ── Logos de aliados (registro cerrado) ──
// Una llave nueva = agregarla aquí + mapear su asset en el PARTNER_LOGOS del
// render (Record exhaustivo verificado por el compilador).
/** Respuesta FLOW-LOCAL que identifica al aliado RECIÉN elegido (la guarda el
 * grid de aliados y por su valor rutean sus edges/visibleWhen); el chrome 'ally'
 * y las pantallas del branch de aliados la leen para resolver marca y copy
 * contra el catálogo del render. NO es el slot canónico: el grid marca
 * `declaresAlly` y el reducer refleja esta selección en la UNIÓN acumulativa
 * `ally_ids` (ALLY_IDS_ANSWER_KEY). */
export const ALLY_ANSWER_KEY = 'ally_selected';

/** Slot canónico de aliados (CANONICAL_PROFILE_KEYS.ally_ids): la UNIÓN
 * acumulativa de todo lo que los nodos `declaresAlly` han elegido. Lo escribe
 * SOLO el mirror del reducer — ningún nodo lo guarda directo. */
export const ALLY_IDS_ANSWER_KEY = 'ally_ids';

/** Correo con el que el usuario conecta su cuenta del aliado. Su presencia es
 * la señal de que YA ENTRÓ a la alianza (el host la lee para apagar
 * `campaignPrefilled`): antes de ella el usuario viene en tránsito desde el
 * link; después, está adentro y el grid vuelve a ser suyo. */
export const ALLY_EMAIL_ANSWER_KEY = 'ally_email';

/** Whether the Zubale partner is already enrolled in Zubale ('yes' | 'no'): the
 * question after the ally email. The home banner reads it to point a partner who
 * is not enrolled yet at the enrollment screen. */
export const ZUBALE_ENROLLED_ANSWER_KEY = 'zubale_inscrito';

/** Whether the Rappi partner already delivers with Rappi ('yes' | 'no'): Rappi's
 * landing step instead of the ally email (no email validation, no T&Cs). It
 * links the Rappi organization and 'yes' opens the Rappi hub. */
export const RAPPI_ENROLLED_ANSWER_KEY = 'rappi_inscrito';

/** Respuesta que guarda el objetivo elegido en el fork raíz del flow. Sus
 * choices definen las RAMAS del onboarding: purchaseContext discrimina el
 * terminal de compra por ella y el mapa del sandbox deriva sus segmentos por
 * alcanzabilidad desde cada choice. */
export const GOAL_ANSWER_KEY = 'goal';

/** Slots de answer que las pantallas puras leen por nombre (PhoneVerification
 * lee el celular, Info interpola el nombre, recommendPlan lee regímenes). El candado
 * de flowData.test exige que algún nodo los guarde — renombrar el `saves` en
 * el JSON truena en CI, no silencioso en runtime. */
export const CELLPHONE_ANSWER_KEY = 'cellphone';
export const FULL_NAME_ANSWER_KEY = 'full_name';
export const REGIMEN_ANSWER_KEY = 'regimen';
export const REG_REGIMES_ANSWER_KEY = 'reg_regimes';
/** Cuánto lleva sin presentar, en rangos. Es la mitad del adeudo DECLARADO
 * —la otra es reg_regimes— y de ahí salen los ejercicios que se cotizan por
 * defecto. Era un string crudo en DeclaredQuoteScreen; vive aquí porque el
 * candado de flowData.test exige que algún nodo lo guarde, y porque ahora es
 * parte de la llave de caché de la cotización. */
export const REG_MONTHS_BEHIND_ANSWER_KEY = 'reg_months_behind';
/** Selección del paquete de regularización: arrays de llaves
 * '<regimenId>:<YYYY-MM>' que emiten los constructores como `data` de su
 * outcome. Son DOS slots hermanos (manual/vinculado) porque los nodos son
 * co-alcanzables estáticamente (duplicate-saves los veta en un solo slot);
 * el ÚNICO lector es readPackageSelection (screens/packageSelection), que los
 * coalesce — nunca leas los slots crudos. */
export const REG_PACKAGE_MANUAL_ANSWER_KEY = 'reg_package';
export const REG_PACKAGE_LINKED_ANSWER_KEY = 'reg_package_linked';

/** Bundle comprado en el purchase del branch de regularización ('package' |
 * 'package,plan' como string[]): la confirmación pinta EXACTAMENTE lo cobrado,
 * no una inferencia por ctx. */
export const REG_PURCHASE_ANSWER_KEY = 'reg_purchase';

/** Asignación del experimento phone-verification-optional (GrowthBook decide
 * UNA vez; el record recuerda para siempre). value = { variant } con
 * 'treatment' | 'control'. La lee useHostCtx (ctx.phoneOptional) y el
 * resolveOnboardingRedirect (regla 4); el análisis la cruza en DB. */
export const PHONE_OTP_EXPERIMENT_ANSWER_KEY = 'phone_otp_experiment';

/** Invoicing-only branch answers, read by name: the host sends the three as
 * props of `onboarding_completed`. The flowData lock requires a node that
 * saves each. */
export const PERSON_TYPE_ANSWER_KEY = 'person_type';
export const MONTHLY_INVOICES_ISSUED_ANSWER_KEY = 'monthly_invoices_issued';
export const HAS_CSD_ANSWER_KEY = 'has_csd';

/** Rol del usuario con Morada Uno (agente/propietario). El banner de
 * confirmación del correo del aliado lo lee para confirmar el rol específico
 * (paridad v2) vía Ally.roleSocioLabels del catálogo del render. */
export const MORADA_ROLE_ANSWER_KEY = 'morada_role';

/** Rol del usuario con AG Enterprises (plataformas/independiente/empresarial).
 * A diferencia de Morada Uno, aquí el rol también decide el PLAN del aliado
 * (Ally.rolePlanIds): el convenio AG Start cubre las tres familias. */
export const AG_ROLE_ANSWER_KEY = 'ag_role';

/** Llaves de rol de aliado, en orden de precedencia: a lo más una tiene valor
 * (los nodos de rol son visibleWhen-exclusivos por aliado). */
/** Rol del usuario que llega por la campaña de Admattic
 * (plataformas/independiente/empresarial). Como en AG, el rol decide el PLAN
 * (Ally.rolePlanIds): la campaña es de alcance masivo por WhatsApp y no sabemos
 * a qué segmento le va a caer, así que se le pregunta en vez de adivinarle.
 *
 * Llave PROPIA y no reusar `ag_role`: los nodos de rol son visibleWhen-
 * exclusivos por aliado, y compartir la llave dejaría a un usuario de Admattic
 * con `ag_role` respondido en su record — una mentira para cualquiera que
 * después cruce esa respuesta en el análisis. */
export const ADMATTIC_ROLE_ANSWER_KEY = 'admattic_role';

/** Rol del usuario que llega por la campaña de Digital Hub. Misma mecánica y
 * mismos planes que Admattic; llave propia por la misma razón que allá — los
 * nodos de rol son visibleWhen-exclusivos por aliado, y compartir la llave
 * dejaría a un usuario de una red con el rol de la otra respondido en su
 * record, una mentira para quien cruce esa respuesta después. */
export const DIGITALHUB_ROLE_ANSWER_KEY = 'digitalhub_role';

/** Servicio que el usuario busca con DiDi: 'activation' (Programa de Activación
 * Fiscal DiDi Last Mile: lista, T&Cs, CURP, hub) o 'declarations' (solo el plan
 * de declaraciones, el flujo estándar de aliado que `didi` tenía antes del
 * programa). Es la llave de ROL de DiDi: decide el content pack, el socio label
 * y el hero copy como en Morada Uno/AG, y ADEMÁS si el programa de cumplimiento
 * aplica (Ally.compliance.intent, resuelto por resolveAllyCompliance). El
 * deep-link `?partner=didi` la pre-estampa en 'activation' y su nodo es
 * campaign-owned (visibleWhen campaignPrefilled=false), así que el link que
 * DiDi reparte a sus repartidores nunca ve la pregunta; quien entra por el
 * grid —el del onboarding o el de «Aliados oficiales» / perfil / hito— la
 * contesta cada vez que pasa por él (cambio de opinión de t-076). Sin
 * respuesta guardada el programa NO aplica (los nodos del programa exigen
 * `eq 'activation'`, igual que resolveAllyCompliance): un record de DiDi sin
 * intención es un usuario estándar hasta que elige. NO se oculta por su
 * propia respuesta: la visibilidad de un nodo no puede depender de lo que él
 * mismo guarda (rompe el replay posicional del walk; el candado de modelWalk
 * lo rechaza). */
export const DIDI_INTENT_ANSWER_KEY = 'didi_intent';

/** Rol del usuario que llega por la campaña de Calcu mx. Mismos tres perfiles y
 * mismos planes que Admattic y Digital Hub, y llave propia por la misma razón.
 *
 * Lo que cambia es QUIÉN la contesta: la calculadora que el usuario acaba de
 * usar ya dijo su régimen, así que el anuncio lo manda en `?calc=` y el
 * deep-link pre-estampa esta llave. El nodo existe para el visitante que llega
 * al link pelado (alguien lo compartió, o el anuncio perdió el param): el walk
 * lo pasa de largo cuando la respuesta ya viene estampada, así que no se oculta
 * con `campaignPrefilled` — el prestamp ya lo salta y ocultarlo dejaría sin
 * pregunta a quien sí la necesita. */
export const CALCUMX_ROLE_ANSWER_KEY = 'calcumx_role';

/** Rol del usuario que llega por el link de TUAL (app para médicos). */
export const TUAL_ROLE_ANSWER_KEY = 'tual_role';

/** Régimen fiscal del usuario que llega por el link de PLATA (banco). */
export const PLATA_ROLE_ANSWER_KEY = 'plata_role';

/** Rol del usuario que llega por el link de Maslow (app de beneficios para
 * empleados). El link es UNO solo para los tres regimenes, asi que el usuario
 * contesta la pregunta de rol como en Digital Hub (sin `campaignRole`). Llave
 * propia por la misma razon que las demas: los nodos de rol son
 * visibleWhen-exclusivos por aliado. */
export const MASLOW_ROLE_ANSWER_KEY = 'maslow_role';

/** Qué vende el link de campaña de un aliado con `annualOffer`: 'annual' (la
 * declaración anual, `?offer=annual`) o 'subscription' (el plan). Lo estampa la
 * captura del aliado; ningún nodo lo pregunta. Con 'annual' el flow se salta la pregunta de
 * rol y las capacidades del plan, y la pantalla del plan muestra la tarjeta de la
 * anual. Siempre se estampa (también 'subscription') para que el link del plan
 * pise a uno anual anterior. */
export const CAMPAIGN_OFFER_ANSWER_KEY = 'campaign_offer';
export type CampaignOffer = 'annual' | 'subscription';

export const ALLY_ROLE_ANSWER_KEYS = [
	MORADA_ROLE_ANSWER_KEY,
	AG_ROLE_ANSWER_KEY,
	ADMATTIC_ROLE_ANSWER_KEY,
	DIGITALHUB_ROLE_ANSWER_KEY,
	CALCUMX_ROLE_ANSWER_KEY,
	TUAL_ROLE_ANSWER_KEY,
	PLATA_ROLE_ANSWER_KEY,
	MASLOW_ROLE_ANSWER_KEY,
	DIDI_INTENT_ANSWER_KEY,
] as const;

export const PARTNER_LOGO_KEYS = [
	// Marcas de adquisición (pills de discovery_source), no aliados:
	'google',
	'tiktok',
	'facebook',
	'instagram',
	'linkedin',
	// Aliados:
	'admattic',
	'digitalhub',
	'calcumx',
	'tual',
	'maslow',
	'ag',
	'uber',
	'rappi',
	'didi',
	'zubale',
	'deel',
	'airbnb',
	'didi-food',
	'kovi',
	'mercado-envios',
	'mercado-libre',
	'uber-eats',
	'morada-uno',
	'plata',
	'boosmap',
	'doctoralia',
	'lalamove',
	'terapify',
	'walmart',
	'workana',
] as const;
export type PartnerLogoKey = (typeof PARTNER_LOGO_KEYS)[number];

// ── Agrupación visual (solo mapa dev) ──
// Nodos con el mismo `group` se colapsan en UNA tarjeta del canvas de flujo y
// su subflujo se abre al click (p.ej. los 4 nodos de una vinculación SAT).
// Presentación pura: walk, reducer y validateFlow lo ignoran por completo.
const groupField = z.string().optional();

// ── Contenido compartido ──
const choiceSchema = z
	.object({
		label: z.string(),
		emoji: z.string().optional(),
		description: z.string().optional(),
		/** Fila de logos de aliados en lugar de la descripción (card de goals). */
		logos: z.array(z.enum(PARTNER_LOGO_KEYS)).optional(),
		/** Logo único como visual principal del choice. Cuando TODOS los choices
		 * de un single lo traen, la pantalla se pinta como grid de logos
		 * (pantalla de aliados). */
		logo: z.enum(PARTNER_LOGO_KEYS).optional(),
		/** Card fantasma: borde punteado, texto centrado ("Solo quiero explorar"). */
		ghost: z.boolean().optional(),
		/** En multi: seleccionarla des-selecciona el resto ("Ninguna de estas"). */
		exclusive: z.boolean().optional(),
		/** Choice colapsada tras el toggle `moreLabel` del nodo ("Ver más
		 * regímenes") — las primarias se muestran siempre. */
		secondary: z.boolean().optional(),
		/** Elección de UNA pasada: la hidratación la olvida (el usuario re-elige
		 * al volver). Solo para salidas de baja intención ("Solo quiero
		 * explorar"). validateFlow la restringe a variant 'single' — la
		 * hidratación solo olvida respuestas single (ver forgetTransientAnswers). */
		transient: z.boolean().optional(),
		/** Offers the choice only while a ctx key holds a value (a goal behind a
		 * feature flag the host resolves into ctx). Ctx-only on purpose: an
		 * answer-based condition would make a choice appear or vanish mid-flow
		 * and pull more answer keys into the replay contract. The runner drops a
		 * hidden choice from the screen and the reducer rejects it as an answer,
		 * so the screen and the model cannot disagree. */
		visibleWhen: z.object({ ctx: z.enum(CTX_KEYS), eq: literal }).strict().optional(),
	})
	.strict();

// ── Top content de preguntas (registro cerrado) ──
// Bloque registrado que se pinta ARRIBA de una pregunta (p.ej. el resumen vivo
// de respuestas del diagnóstico). Llave nueva = entrada aquí + componente en el
// TOP_CONTENT del render (Record exhaustivo verificado por el compilador).
export const TOP_CONTENT_KEYS = ['diagnostic-answers'] as const;
export type TopContentKey = (typeof TOP_CONTENT_KEYS)[number];

// ── Iconos de bullets (registro cerrado) ──
// Una llave nueva = agregarla aquí + mapearla en el INFO_ICONS del render;
// el Record exhaustivo sobre este union hace que el compilador exija ambos.
export const INFO_ICON_KEYS = [
	'receipt',
	'chart-bar',
	'wrench',
	'chat',
	'clock-back',
	'envelope',
	'thumbs-up',
] as const;
export type InfoIconKey = (typeof INFO_ICON_KEYS)[number];

// ── Bullet avatars (closed registry) ──
// Marks a bullet that IS a person ("tu contador de heru te contacta"). The
// render does not map keys to images: any key paints the user's CRM account
// owner (useAdvisor) instead of an icon, with a neutral placeholder while it
// loads or if there is no owner. Keys only name the avatar in the flow JSON.
export const INFO_AVATAR_KEYS = ['contador'] as const;
export type InfoAvatarKey = (typeof INFO_AVATAR_KEYS)[number];

// ── Ilustraciones de pantalla (registro cerrado) ──
// Bloques visuales animados puros (SVG + motion, cero IO) registrados por
// llave. Los consumen los nodos info (`illustration`: centrada arriba del
// título en 'hero', bajo el body en 'step') y las questions single/multi
// (compacta entre el título y las choices). Mismo trato que los iconos:
// llave nueva = entrada aquí + componente en el FLOW_ILLUSTRATIONS del render
// (Record exhaustivo verificado por el compilador).
export const ILLUSTRATION_KEYS = [
	'email-sent',
	'linking-background',
	'accountant-chase',
] as const;
export type IllustrationKey = (typeof ILLUSTRATION_KEYS)[number];

// ── Escenas de loader (registro cerrado) ──
// The picture of a told wait (`loader.scene`): a pure scene driven by the
// state of the loader's three steps. Same treatment as the illustrations: a
// new key = an entry here + its component in the LOADER_SCENES of the render
// (exhaustive Record checked by the compiler).
export const LOADER_SCENE_KEYS = ['seals-connect'] as const;
export type LoaderSceneKey = (typeof LOADER_SCENE_KEYS)[number];

// ── Nodos ──
const questionNode = z
	.object({
		kind: z.literal('question'),
		variant: z.enum(['single', 'multi', 'text']),
		/** Pinta las choices como PILLS compactas (chips en flex-wrap) en vez de
		 * tarjetas apiladas, para preguntas de escaneo rápido como «¿Cómo
		 * conociste heru?». En variant 'single' el tap responde y avanza; en
		 * 'multi' alterna la selección y el Continuar sigue mandando — ahí el
		 * chip además dibuja su propio check, porque una pregunta de varias
		 * respuestas tiene que dejar ver todo lo marcado sin scrollear. Las
		 * descripciones de las choices NO se pintan en este modo. */
		pills: z.boolean().optional(),
		/** Single only: the choices are the ordered stops of a slider, in the
		 * order of `choices`, and `unit` follows the picked label («6 a 20
		 * facturas al mes»). It starts with no stop picked and «Continuar» sends
		 * the pick, so the first stop is never an answer by default. Labels only:
		 * emojis and descriptions would not be painted (validateFlow). */
		slider: z.object({ unit: z.string() }).strict().optional(),
		/** Another question of the flow, asked on this same screen under the
		 * slider and optional. Its title, choices and `saves` are that node's, so
		 * the pick lands in the same slot with the same values (inv_volume asks
		 * diag_occupation's «¿A qué te dedicas?»). It must be a plain 'multi' on a
		 * branch that never meets this node (validateFlow): both write the slot.
		 * An empty pick leaves the slot unanswered. */
		companion: z.string().optional(),
		/** Choices of the companion this screen does not offer (inv_volume
		 * leaves «Empleado» out of «¿A qué te dedicas?»). The companion's own
		 * screen keeps them; here the screen and the reducer both drop them,
		 * because both read the companion through companionOf. */
		companionOmits: z.array(z.string()).min(1).optional(),
		/** Pinta el wordmark heru arriba y centra el título (pantalla de goals). */
		logo: z.boolean().optional(),
		title: z.string(),
		subtitle: z.string().optional(),
		/** Descripción bajo el título ("Te enviaremos un código por SMS…"). */
		body: z.string().optional(),
		/** Requerido para single/multi. */
		choices: z.record(z.string(), choiceSchema).optional(),
		/** Label del toggle que revela las choices `secondary` ("Ver más
		 * regímenes"). validateFlow exige el par completo: secondary ⇄ moreLabel. */
		moreLabel: z.string().optional(),
		/** Bloque registrado arriba de la pregunta (resumen de respuestas del
		 * diagnóstico). */
		topContent: z.enum(TOP_CONTENT_KEYS).optional(),
		/** Llave bajo la que se guarda la respuesta en state.answers. */
		saves: z.string(),
		/** El nodo declara regímenes: al responderse, el reducer escribe TAMBIÉN
		 * el slot canónico `regimen` con la UNIÓN normalizada de lo elegido
		 * (mismo mirror que buildRegimenMirror de v2). Sus choices deben
		 * normalizar a CanonicalRegime o a un sentinel (lo verifica validateFlow).
		 * Convergencia al slot canónico para preguntas de régimen no-canónicas
		 * (p.ej. reg_regimes). */
		declaresRegimes: z.boolean().optional(),
		/** El nodo declara aliados: al responderse, el reducer refleja la selección
		 * en el slot canónico `ally_ids` como UNIÓN acumulativa (existente ∪
		 * elegido, orden estable, sin duplicados) — mismo mirror que declaresRegimes
		 * pero SIN normalización (los slugs de aliado son abiertos). El answer del
		 * nodo se queda single y sus edges rutean por él (ALLY_ANSWER_KEY);
		 * validateFlow exige que el nodo tenga choices y que el flow pueda declarar
		 * `ally_ids` en collects. Segundo consumidor del patrón mirror. */
		declaresAlly: z.boolean().optional(),
		/** Solo variant text. */
		placeholder: z.string().optional(),
		/** Semántica del input text: 'email' activa teclado/autocomplete de
		 * correo, validación de formato y sugerencia de typo de dominio; 'curp'
		 * normaliza a mayúsculas sin separadores, topa en 18 y valida el patrón
		 * de CURP. */
		format: z.enum(['email', 'curp']).optional(),
		/** Pinta la fila de aceptación de Términos y Condiciones bajo el input
		 * y bloquea el submit hasta aceptar (pre-aceptada vía ctx.acceptedTcs). */
		terms: z.boolean().optional(),
		/** Text de párrafo (textarea + CTA propio, la pantalla de texto libre del
		 * diagnóstico) en lugar del input de subrayado de una línea. */
		multiline: z.boolean().optional(),
		/** La respuesta vacía es válida (el CTA continúa sin texto). */
		optional: z.boolean().optional(),
		/** Tope de caracteres del text (el reducer lo valida — no solo la UI). */
		maxLength: z.number().int().positive().optional(),
		/** Línea de ayuda bajo el input ("Opcional — puedes continuar"). */
		helper: z.string().optional(),
		/** Ejemplos de solo lectura bajo el textarea (inspiración, no precargan). */
		examples: z.array(z.string()).min(1).optional(),
		examplesLabel: z.string().optional(),
		/** Label del CTA del text multiline. Default: "Continuar". */
		cta: z.string().optional(),
		/** Selección inicial de una pregunta 'multi' desde ctx: la llave trae los
		 * valores separados por coma y sólo los que existen como choice se marcan.
		 *
		 * Es PRE-SELECCIÓN, no respuesta: el nodo sigue sin contestar, así que el
		 * walk se para en él y el usuario confirma o corrige. Estampar la
		 * respuesta directo lo saltaría, y una extracción puede traer un régimen
		 * de más (sueldos y salarios) o de menos. */
		prefillChoices: z.enum(CTX_KEYS).optional(),
		/** Valor inicial del input desde ctx (solo variant text). En revisitas
		 * gana answers[saves]; ctx[prefill] llena la primera visita. */
		prefill: z.enum(CTX_KEYS).optional(),
		/** Efecto declarado al responder: el reducer lo devuelve en `effects` y
		 * el host lo ejecuta (p.ej. persistir el nombre en el user profile). */
		sync: z.enum(SYNC_KEYS).optional(),
		/** Limita el sync a una respuesta (variant single): con otra respuesta no
		 * hay efecto. Debe ser una de sus choices (validateFlow lo verifica). */
		syncWhen: z.string().optional(),
		/** Validación-escritura PRE-submit (ver GATE_KEYS): la pantalla la corre
		 * vía services y solo avanza si resuelve ok; un rechazo se pinta inline.
		 * 'profile-email' exige variant text + format 'email'; 'ally-link-verify'
		 * exige variant text + chrome 'ally' (validateFlow los verifica). */
		gate: z.enum(GATE_KEYS).optional(),
		/** Chrome de marca del aliado elegido (branch de aliados): la pantalla se
		 * envuelve en el hero del aliado (answers[ALLY_ANSWER_KEY] resuelto contra
		 * el catálogo del render) con la card blanca flotante del flujo v2. Los
		 * textos del nodo pueden interpolar `{ally}` con el nombre del aliado.
		 * Aplica a single y text; validateFlow exige que el flow recolecte
		 * ALLY_ANSWER_KEY. */
		chrome: z.enum(['ally']).optional(),
		visibleWhen: conditionSchema.optional(),
		// Sin `group`: hoy ningún question vive agrupado (los subflujos de
		// vinculación SAT son actions+infos). Si un flow lo necesita, se
		// re-agrega aquí junto con su consumidor — capacidad sin uso se borra.
		next: nextSchema,
		skip: skipSchema,
	})
	.strict();

const infoNode = z
	.object({
		kind: z.literal('info'),
		/** Layout: 'hero' (default — centrado, logo/video/bullets, CTA sticky,
		 * como la welcome) | 'step' (layout de paso del QuestionStep legacy:
		 * alineado a la izquierda, centrado vertical, submit circular). */
		variant: z.enum(['hero', 'step']).optional(),
		/** Pinta el wordmark heru arriba (pantallas de entrada al producto). */
		logo: z.boolean().optional(),
		title: z.string(),
		body: z.string().optional(),
		/** A line in bold right under the body, on its own line: the takeaway of
		 * the screen («Si no los tienes, heru te ayuda a generarlos.»). Hero
		 * layout only and only next to a body (validateFlow). */
		emphasis: z.string().optional(),
		/** A told wait: a registered scene on the clock of invoicing's stamping
		 * scene, three numbered steps that light one every two seconds, the title
		 * and body as its copy, and the CTA off until the last step lights. Hero
		 * layout, and nothing else on the screen (validateFlow). */
		loader: z
			.object({
				scene: z.enum(LOADER_SCENE_KEYS),
				steps: z.tuple([z.string(), z.string(), z.string()]),
			})
			.strict()
			.optional(),
		/** Bloque visual registrado de la pantalla: bajo el body en 'step',
		 * centrado bajo el título/body en 'hero' (título primero, igual que en
		 * las questions). */
		illustration: z.enum(ILLUSTRATION_KEYS).optional(),
		/** Card de bullets con icono, opcionalmente con encabezado
		 * ("Con nosotros puedes" / "Te ayudamos a"). Un item con description
		 * se pinta como highlight (título + detalle, como el intro de flujo).
		 * `variant: 'steps'` pinta los items como timeline conectado (badges
		 * circulares unidos por una línea que se dibuja, el primero latiendo) —
		 * para procesos secuenciales, no listas. `avatar` en un item pinta un
		 * rostro registrado en lugar del icono. */
		bullets: z
			.object({
				title: z.string().optional(),
				variant: z.enum(['steps']).optional(),
				items: z
					.array(
						z
							.object({
								icon: z.enum(INFO_ICON_KEYS).optional(),
								avatar: z.enum(INFO_AVATAR_KEYS).optional(),
								label: z.string(),
								description: z.string().optional(),
							})
							.strict(),
					)
					.min(1),
			})
			.strict()
			.optional(),
		/** Tabla comparativa de dos columnas ("lo que hace tu contador" vs "lo que
		 * hace heru"). Es CONTENIDO, igual que bullets: la pantalla de info la
		 * pinta y el flow decide qué filas van — agregar una comparación es
		 * agregar data, nunca forkear una pantalla.
		 *
		 * `mine`/`ours` son booleanos a propósito: la fuerza del bloque está en
		 * ver la columna llena contra la vacía. Una celda con texto libre
		 * invitaría a párrafos y el bloque dejaría de leerse de un vistazo. */
		comparison: z
			.object({
				mineLabel: z.string(),
				oursLabel: z.string(),
				rows: z
					.array(
						z
							.object({
								label: z.string(),
								mine: z.boolean(),
								ours: z.boolean(),
							})
							.strict(),
					)
					.min(1),
			})
			.strict()
			.optional(),
		/** Video vertical (YouTube) con caption pill debajo. */
		media: z
			.object({ videoId: z.string(), caption: z.string().optional() })
			.strict()
			.optional(),
		/** Label del CTA principal. Default: "Continuar". */
		cta: z.string().optional(),
		/** Detour: the CTA RETURNS to `next`, the question whose choice opened
		 * this screen, and forgets that question's answer so the user answers it
		 * again (the explainer behind «No sé qué son»). A forward edge there would
		 * be a cycle; this one is a return, not a step, so validateFlow keeps it
		 * out of the DAG check and requires `next` to be a question that leads
		 * here. The node is never marked seen, so the replay never follows it. */
		returns: z.literal(true).optional(),
		/** On a `returns` detour, CTAs that answer the question it returns to:
		 * choice key → button label, in order, the first one primary. Tapping
		 * one is answering that question with that choice from here (the
		 * explainer's «Sí, tengo mis sellos» answers «¿Ya tienes tus sellos?»
		 * with yes), so the answer lands in the question's own slot and the walk
		 * goes on exactly as if it was answered there. They replace `cta`. */
		returnAnswers: z.record(z.string(), z.string()).optional(),
		visibleWhen: conditionSchema.optional(),
		group: groupField,
		next: nextSchema,
		skip: skipSchema,
	})
	.strict();

const actionNode = z
	.object({
		kind: z.literal('action'),
		/** Nombre en el ACTION registry del runtime (sat-linking, purchase, …).
		 * El registry declara los outcomes tipados del action; validateFlow
		 * exige que `next` cubra exactamente esos outcomes. */
		action: z.string(),
		/** Slot de answer donde el reducer guarda el `data` que acompaña al
		 * outcome (la selección del constructor de paquete, el bundle comprado).
		 * Mismo contrato de replay que el `saves` de una question: el resume y
		 * las pantallas downstream lo leen por nombre; un skip lo borra. */
		saves: z.string().optional(),
		/** El interior pide aceptar Términos y Condiciones (pre-aceptada vía
		 * ctx.acceptedTcs — mismo contrato que el `terms` de question). El host
		 * trata la PRIMERA transición exitosa de un nodo `terms` como la
		 * aceptación: estampa heru_tcs en el record y dispara el sign_up. */
		terms: z.boolean().optional(),
		visibleWhen: conditionSchema.optional(),
		group: groupField,
		/** Para action, next SIEMPRE es mapa outcome → nodeId. */
		next: z.record(z.string(), z.string()),
		skip: skipSchema,
	})
	.strict();

// Sets CERRADOS (typo en un flow = falla de parseo en CI, no fallback
// silencioso en runtime). Un terminal o atribución nuevos = agregar la llave
// aquí + su render/consumidor, igual que ACTION_CONTRACTS. El `terminal` de un
// nodo elige su pantalla en el TERMINAL registry del runtime: 'home' cae a la
// pantalla genérica (cierre neutro), los ricos ('plan-recommendation') a su
// pantalla propia. La paridad flows ⇄ registry la exige terminalRegistry.test.
// El ÉXITO DE COMPRA no es terminal: es estado efímero del drawer de checkout
// (nunca una posición, nunca persistido) y su continuar sale a home.
// 'invoicing' exits like 'home' but lands on invoicing: the host picks the
// route from the answers and the live SAT link (see EXIT_TERMINAL_ROUTES).
export const TERMINAL_KEYS = ['home', 'plan-recommendation', 'invoicing'] as const;
export type TerminalKey = (typeof TERMINAL_KEYS)[number];
export const COMPLETED_VIA_KEYS = ['onboarding'] as const;

const terminalNode = z
	.object({
		kind: z.literal('terminal'),
		/** Pantalla de cierre en el TERMINAL registry. Llegar aquí ES
		 * completarse. */
		terminal: z.enum(TERMINAL_KEYS),
		/** Atribución de completion estampada por la transición (viaja en el
		 * evento de completed). */
		completedVia: z.enum(COMPLETED_VIA_KEYS),
	})
	.strict();

export const nodeSchema = z.discriminatedUnion('kind', [
	questionNode,
	infoNode,
	actionNode,
	terminalNode,
]);

// ── Flow ──
export const flowSchema = z
	.object({
		flow: z.string(),
		/** Versión inmutable del archivo. Cambiar el flow = versión nueva. */
		version: z.string(),
		/** Manifiesto de slots canónicos del perfil que este flow recolecta
		 * (CANONICAL_PROFILE_KEYS). validateFlow lo cruza contra los `saves`
		 * reales: todo key declarado tiene un nodo que lo escribe con choices
		 * dentro del enum canónico, y ningún nodo escribe un slot canónico sin
		 * declararlo. La única fuente de "¿qué mide este flow?". */
		collects: z.array(z.string()).optional(),
		start: z.string(),
		nodes: z.record(z.string(), nodeSchema),
	})
	.strict();

export type ChoiceDef = z.infer<typeof choiceSchema>;
export type QuestionNode = z.infer<typeof questionNode>;
export type InfoNode = z.infer<typeof infoNode>;
export type ActionNode = z.infer<typeof actionNode>;
export type TerminalNode = z.infer<typeof terminalNode>;
export type NodeDef = z.infer<typeof nodeSchema>;
export type FlowDef = z.infer<typeof flowSchema>;

// ── Estado y transición (el contrato que hoy corre client-side y en fase 0b
//    se muda a heru-user-management-engine — misma función, otro dueño) ──
export type AnswerValue = string | string[] | boolean;

export type FlowState = {
	/** Nodo actual, o null si el flow no ha iniciado. */
	position: string | null;
	answers: Record<string, AnswerValue>;
	completed: boolean;
	completedVia: string | null;
};

export type TransitionInput =
	| { type: 'start' }
	/** `companion`: the pick of the node's companion question, empty or absent
	 * when the user picked nothing (see `companion` on question nodes). */
	| { type: 'answer'; value: AnswerValue; companion?: string[] }
	/** `data`: payload del outcome — el reducer lo guarda bajo el `saves` del
	 * nodo action (si lo declara). Ignorado en actions sin saves. */
	| { type: 'outcome'; value: string; data?: AnswerValue }
	/** `answer`: on a `returns` info with `returnAnswers`, the choice the
	 * question it returns to is answered with. */
	| { type: 'continue'; answer?: string } // info CTA
	| { type: 'skip' }
	| { type: 'back' };

// ── Validación de respuestas de texto (única fuente: la comparten el reducer
//    y las pantallas; en fase 0b el server ejecuta exactamente estas reglas) ──
/** Paridad con el MIN_NAME_LENGTH del ProfileStep legacy. */
export const MIN_TEXT_ANSWER_LENGTH = 2;
/** Paridad con el EMAIL_REGEX del EmailStep legacy. */
export const EMAIL_ANSWER_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** El MISMO regex que valida el CURP en la pantalla de vinculación: un CURP
 * capturado en el onboarding y uno capturado al vincular no pueden diferir en
 * qué consideran válido. Se importa en vez de copiarse. */
export const CURP_ANSWER_REGEX = REGEX.curpRegex;
/** Contrato del `data` del outcome 'verified' del action 'phone-verification':
 * el celular en la forma persistida "+<lada> <10 dígitos>" (p.ej.
 * "+52 5512345678"). Lo produce formatPhoneAnswer; el candado de phoneAnswer.test
 * afirma que su salida cumple este regex. */
export const PHONE_ANSWER_REGEX = /^\+\d{1,3} \d{10}$/;

export type EmittedEvent = {
	name: string;
	payload: Record<string, string | number | boolean | null>;
};

/** Escritura externa declarada por el nodo respondido; el dueño del estado la
 * ejecuta después de aplicar la transición. */
export type EffectCommand = { sync: SyncKey; value: AnswerValue };

export type TransitionError = 'NOT_AT_NODE' | 'ILLEGAL_EDGE' | 'ALREADY_COMPLETED';

export type TransitionResult =
	| { ok: true; state: FlowState; events: EmittedEvent[]; effects: EffectCommand[] }
	| { ok: false; error: TransitionError; state: FlowState };

/** Outcomes tipados que un action puede emitir; el registry del runtime los
 * declara y validateFlow los cruza contra el `next` del nodo.
 *
 * Aquí VIVÍA `irreversible`, que sacaba al nodo del path para siempre en cuanto
 * grababa un outcome. Se quitó (31-ago-2026): ninguna pantalla se esconde por
 * un hecho del pasado. Si una no debe mostrarse, lo dice el estado VIVO en su
 * `visibleWhen` —`hasActivePlan` para una compra de plan, `isLinked` para la
 * vinculación—, que se puede volver a consultar y se corrige solo. Ver
 * walk.isLanding para el porqué. */
export type ActionContract = {
	outcomes: readonly string[];
	/** Outcomes que la RE-ENTRADA olvida (hostState los limpia al reconstruir):
	 * decisiones válidas para UNA pasada —declinar un plan— que al recargar
	 * deben volver a preguntarse en vez de que el fast-forward pase de largo
	 * replayeándolas. Un outcome que sella un hecho (purchased) NUNCA va aquí:
	 * quien compró no tiene por qué volver a ver la pantalla al recargar.
	 * Subconjunto de `outcomes` (candado en flowData.test). */
	reconsiderable?: readonly string[];
	/** Outcomes que SALEN del onboarding sin completar (explorar): el dueño del
	 * estado (host/sandbox) los ejecuta como PUERTA DE SALIDA vía isExitOutcome
	 * ANTES de aplicar la transición — estampa presencia 'exited' y navega a
	 * home. El `next` del nodo NO los cubre (validateFlow lo exige) y su outcome
	 * JAMÁS se persiste. Un nodo action con `exits` no vacío cuenta como sink
	 * legal (salir ES un final): validateFlow lo siembra en la alcanzabilidad de
	 * terminal. Subconjunto de `outcomes` (candado en flowData.test). */
	exits?: readonly string[];
};

export const parseFlow = (raw: unknown): FlowDef => flowSchema.parse(raw);
